import { Tag } from "@redotech/redo-common-model/tag";
import { TimeUnit } from "@redotech/redo-common-model/time";
import { BaseStep, StepType } from "@redotech/redo-flows-core-common/common";
import { Service } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { Carrier } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { Hazmat } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-common/fulfillment-delivery-method-type";
import { FulfillmentOrderAddressWithId } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { SortingCriteria } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { ShopifyFulfillmentHoldReason } from "@redotech/redo-fulfillment-group-common/shopify-fulfillment-schemas";
import { PageOrientation } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { Weight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { OutboundParcel } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { SpecialRateEligibility } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { PageSize } from "../../packing-slips/packing-slip-builder";

export enum OmsActionType {
  SET_SHIPPING_SERVICE = "SET_SHIPPING_SERVICE",
  SET_PARCEL = "SET_PARCEL",
  SET_SHIPPING_SERVICE_CHEAPEST = "SET_SHIPPING_SERVICE_CHEAPEST",
  SET_SHIPPING_SERVICE_FASTEST = "SET_SHIPPING_SERVICE_FASTEST",
  SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS = "SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS",
  SET_PACKING_SLIP_TEMPLATE = "SET_PACKING_SLIP_TEMPLATE",
  SET_THIRD_PARTY_BILLING_ACCOUNT = "SET_THIRD_PARTY_BILLING_ACCOUNT",
  ADD_TAG = "ADD_TAG",
  SET_SHIPPING_OPTIONS = "SET_SHIPPING_OPTIONS",
  PLACE_HOLD = "PLACE_HOLD",
  PLACE_HOLD_UNTIL = "PLACE_HOLD_UNTIL",
  SET_WEIGHT = "SET_WEIGHT",
  ROUND_WEIGHT = "ROUND_WEIGHT",
  SET_ASSIGNED_USER = "SET_ASSIGNED_USER",
  SET_DUTIES_PAYER = "SET_DUTIES_PAYER",
  SET_TAX_IDENTIFIERS = "SET_TAX_IDENTIFIERS",
  SET_RETURN_ADDRESS = "SET_RETURN_ADDRESS",
  ASSIGN_FULFILLMENT_SERVICE = "ASSIGN_FULFILLMENT_SERVICE",
  SET_ORIGIN_LOCATION = "SET_ORIGIN_LOCATION",
  SET_BUY_RETURN_LABEL = "SET_BUY_RETURN_LABEL",
  SET_DELIVERY_METHOD = "SET_DELIVERY_METHOD",
  SET_OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS = "SET_OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS",
  GENERATE_PERSONALIZED_SLIP = "GENERATE_PERSONALIZED_SLIP",
  SET_INTERNAL_NOTE = "SET_INTERNAL_NOTE",
  SPLIT_INTO_PARCELS = "SPLIT_INTO_PARCELS",
  ADD_ORDER_TAGS = "ADD_ORDER_TAGS",
  SET_SEND_LABEL_PURCHASED_NOTIFICATION = "SET_SEND_LABEL_PURCHASED_NOTIFICATION",
}
/**
 * This model assumes actions will take the form of "Set ____ to ____"
 * This won't be the case for future actions that get added, so feel free to
 * edit this interface to have an "actionType" or something
 * search-tag:ADDING-AN-OMS-ACTION
 */
export interface BaseOmsActionStep extends BaseStep {
  type: StepType.OMS_ACTION;
  actionType: OmsActionType;
  field: OmsSettableFields;
  nextId: string;
}

export interface SetShippingServiceActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.SHIPPING_SERVICE;
  actionType: OmsActionType.SET_SHIPPING_SERVICE;
  newShippingService: Service;
  newShippingCarrier: Carrier;
  carrierAccountId?: string | null;
}

export interface SetShippingServiceCheapestActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.SHIPPING_SERVICE_CHEAPEST;
  actionType: OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST;
  servicesToChooseFrom: {
    service: Service;
    carrier: Carriers;
    carrierAccountId?: string | null;
  }[];
  servicesToPrioritize?: {
    service: Service;
    carrier: Carriers;
    carrierAccountId?: string | null;
  }[];
  maxPriorityPriceDifference?: number;
  selectionCriteria: SortingCriteria.CHEAPEST;
}

export interface SetShippingServiceFastestActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.SHIPPING_SERVICE_FASTEST;
  actionType: OmsActionType.SET_SHIPPING_SERVICE_FASTEST;
  servicesToChooseFrom: {
    service: Service;
    carrier: Carriers;
    carrierAccountId?: string | null;
  }[];
  servicesToPrioritize?: {
    service: Service;
    carrier: Carriers;
    carrierAccountId?: string | null;
  }[];
  maxPriorityPriceDifference?: number;
  selectionCriteria: SortingCriteria.FASTEST;
}

export interface SetShippingServiceCheapestWithinDeliveryDaysActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS;
  actionType: OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS;
  servicesToChooseFrom: {
    service: Service;
    carrier: Carriers;
    carrierAccountId?: string | null;
  }[];
  servicesToPrioritize?: {
    service: Service;
    carrier: Carriers;
    carrierAccountId?: string | null;
  }[];
  maxPriorityPriceDifference?: number;
  targetDeliveryDays: number;
  selectionCriteria: SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS;
}

export interface SetParcelActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.PARCEL;
  actionType: OmsActionType.SET_PARCEL;
  /** @deprecated  use parcels instead */
  parcel?: OutboundParcel | null;
  parcels?: OutboundParcel[] | null;
  addToExistingParcels?: boolean | null;
}

export interface SetPackingSlipTemplateActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.PACKING_SLIP_TEMPLATE;
  actionType: OmsActionType.SET_PACKING_SLIP_TEMPLATE;
  packingSlipName: string;
  packingSlipTemplateId: string;
}

export interface SetThirdPartyBillingAccountActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT;
  actionType: OmsActionType.SET_THIRD_PARTY_BILLING_ACCOUNT;
  thirdPartyBillingAccountId: string;
  thirdPartyBillingAccountName: string;
}

export interface AddTagActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.TAG;
  actionType: OmsActionType.ADD_TAG;
  tags: Tag[];
}

export interface AddOrderTagsActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.ORDER_TAG;
  actionType: OmsActionType.ADD_ORDER_TAGS;
  tags: string[];
}

export interface ShippingOptions {
  signatureRequired?: boolean | null;
  adultSignatureRequired?: boolean | null;
  includeInsurance?: boolean | null;
  specialRateEligibility?: SpecialRateEligibility | null;
  hazmat?: Hazmat | null;
  dryIceWeight?: Weight | null;
  overrideResidentialAddress?: boolean | null;
}

export interface SetShippingOptionsActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.SHIPPING_OPTIONS;
  actionType: OmsActionType.SET_SHIPPING_OPTIONS;
  shippingOptions: ShippingOptions;
}

export enum AutoHoldType {
  Date = "date",
  Duration = "duration",
  Indefinite = "indefinite",
}

export interface PlaceHoldActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.PLACE_HOLD;
  actionType: OmsActionType.PLACE_HOLD;
  holdOptions: {
    holdType: AutoHoldType.Duration | AutoHoldType.Indefinite;
    duration?: number; // optional when indefinite
    timeUnit?: TimeUnit; // optional when indefinite
    holdReason: ShopifyFulfillmentHoldReason;
    reasonNotes?: string | null;
  };
}

export interface PlaceHoldUntilActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.PLACE_HOLD_UNTIL;
  actionType: OmsActionType.PLACE_HOLD_UNTIL;
  holdOptions: {
    holdType: AutoHoldType.Date;
    date: Date;
    holdReason: ShopifyFulfillmentHoldReason;
    reasonNotes?: string | null;
  };
}

export interface SetWeightActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.SET_WEIGHT;
  actionType: OmsActionType.SET_WEIGHT;
  weight: Weight;
}

export enum WeightRoundingType {
  ROUND_UP = "ROUND_UP",
  ROUND_DOWN = "ROUND_DOWN",
}

export interface RoundWeightActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.ROUND_WEIGHT;
  actionType: OmsActionType.ROUND_WEIGHT;
  roundingType: WeightRoundingType;
  minimumWeight: Weight; // Must be >= 1lb equivalent
}

export interface SetAssignedUserActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.ASSIGNED_USER;
  actionType: OmsActionType.SET_ASSIGNED_USER;
  userId: string;
}

export interface SetDutiesPayerActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.DUTIES_PAYER;
  actionType: OmsActionType.SET_DUTIES_PAYER;
  ddp: boolean; // https://support.easypost.com/hc/en-us/articles/360054753472-What-is-the-difference-between-DDP-DAP-and-DDU
}

export interface SetTaxIdentifiersActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.TAX_IDENTIFIERS;
  actionType: OmsActionType.SET_TAX_IDENTIFIERS;
  taxIdentifierIds: string[];
}

export interface SetReturnAddressActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.RETURN_ADDRESS;
  actionType: OmsActionType.SET_RETURN_ADDRESS;
  address: FulfillmentOrderAddressWithId;
}

export interface AssignFulfillmentServiceActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.FULFILLMENT_SERVICE;
  actionType: OmsActionType.ASSIGN_FULFILLMENT_SERVICE;
  fulfillmentServiceId: string;
  fulfillmentServiceName: string;
}

export interface SetOriginLocationActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.ORIGIN_LOCATION;
  actionType: OmsActionType.SET_ORIGIN_LOCATION;
  locationId: string;
}

export interface SetBuyReturnLabelActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.BUY_RETURN_LABEL;
  actionType: OmsActionType.SET_BUY_RETURN_LABEL;
  buyReturnLabel: boolean;
}

export interface SetDeliveryMethodActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.DELIVERY_METHOD;
  actionType: OmsActionType.SET_DELIVERY_METHOD;
  deliveryMethod: RedoShopifyDeliveryMethodType;
}

export interface SetOverrideUseOriginalDestinationAddressActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS;
  actionType: OmsActionType.SET_OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS;
  overrideUseOriginalDestinationAddress: boolean;
}

export interface GeneratePersonalizedSlipActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.PERSONALIZED_SLIP;
  actionType: OmsActionType.GENERATE_PERSONALIZED_SLIP;
  personalizedSlipTemplateId: string;
  personalizedSlipTemplateName: string;
  /**
   * Overrides the template's default page size when generating the slip PDF.
   * When unset, the template's own `pageLayout.size` is used.
   */
  pageSizeOverride?: PageSize;
  /**
   * Overrides the template's default page orientation when generating the
   * slip PDF. When unset, the template's own `pageLayout.orientation` is used.
   */
  pageOrientationOverride?: PageOrientation;
}

export interface SetInternalNoteActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.INTERNAL_NOTE;
  actionType: OmsActionType.SET_INTERNAL_NOTE;
  note: string;
}

export interface SplitIntoParcelsActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.SPLIT_INTO_PARCELS;
  actionType: OmsActionType.SPLIT_INTO_PARCELS;
}

export interface SetSendLabelPurchasedNotificationActionStep extends BaseOmsActionStep {
  field: OmsSettableFields.SEND_LABEL_PURCHASED_NOTIFICATION;
  actionType: OmsActionType.SET_SEND_LABEL_PURCHASED_NOTIFICATION;
  sendLabelPurchasedNotification: boolean;
}

/**
 * Fields that can be set in an OMS automation action
 * search-tag:ADDING-AN-OMS-ACTION
 */
export enum OmsSettableFields {
  THIRD_PARTY_BILLING_ACCOUNT = "third_party_billing_account",
  SHIPPING_SERVICE = "shipping_service",
  SHIPPING_SERVICE_CHEAPEST = "shipping_service_cheapest",
  SHIPPING_SERVICE_FASTEST = "SHIPPING_SERVICE_FASTEST",
  SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS = "SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS",
  PARCEL = "parcel",
  PACKING_SLIP_TEMPLATE = "packing_slip_template",
  TAG = "tag",
  SHIPPING_OPTIONS = "shipping_options",
  PLACE_HOLD = "place_hold",
  PLACE_HOLD_UNTIL = "place_hold_until",
  SET_WEIGHT = "set_weight",
  ROUND_WEIGHT = "round_weight",
  ASSIGNED_USER = "assigned_user",
  DUTIES_PAYER = "duties_payer",
  TAX_IDENTIFIERS = "tax_identifiers",
  RETURN_ADDRESS = "return_address",
  FULFILLMENT_SERVICE = "fulfillment_service",
  ORIGIN_LOCATION = "origin_location",
  BUY_RETURN_LABEL = "buy_return_label",
  DELIVERY_METHOD = "delivery_method",
  OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS = "override_use_original_destination_address",
  PERSONALIZED_SLIP = "personalized_slip",
  INTERNAL_NOTE = "internal_note",
  SPLIT_INTO_PARCELS = "split_into_parcels",
  ORDER_TAG = "order_tag",
  SEND_LABEL_PURCHASED_NOTIFICATION = "send_label_purchased_notification",
}
